import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useBilling, parseTierLimitError } from "../lib/billing";
import { toast } from "sonner";
import { Plus, Trash2, Upload } from "lucide-react";
import CategoryPicker from "../components/CategoryPicker";
import { readAndDownscaleImage, PHOTO_MAX_BYTES } from "../lib/image";

function toBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function NewProduct() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { openUpgradeModal } = useBilling();
  const [form, setForm] = useState({
    name: "", description: "", categories: [],
    low_stock_threshold: 5, photo_base64: "",
  });
  const [variants, setVariants] = useState([
    { _localId: crypto.randomUUID(), attributes: {}, stock: 0, price: 0, cost: 0 },
  ]);
  const [saving, setSaving] = useState(false);
  if (user && user.role !== "admin") return <Navigate to="/products" replace />;

  const updateVariant = (i, patch) => {
    setVariants((prev) => prev.map((v, idx) => idx === i ? { ...v, ...patch } : v));
  };

  const addAttr = (i) => {
    const key = prompt("Attribute name (e.g., Size, Color):");
    if (!key) return;
    setVariants((prev) => prev.map((v, idx) =>
      idx === i ? { ...v, attributes: { ...v.attributes, [key]: "" } } : v
    ));
  };

  const setAttrValue = (i, k, val) => {
    setVariants((prev) => prev.map((v, idx) =>
      idx === i ? { ...v, attributes: { ...v.attributes, [k]: val } } : v
    ));
  };

  const removeAttr = (i, k) => {
    setVariants((prev) => prev.map((v, idx) => {
      if (idx !== i) return v;
      const next = { ...v.attributes };
      delete next[k];
      return { ...v, attributes: next };
    }));
  };

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > PHOTO_MAX_BYTES) { toast.error("Max 8MB"); return; }
    const b64 = await readAndDownscaleImage(f);
    setForm((p) => ({ ...p, photo_base64: b64 }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/products", {
        ...form,
        variants: variants.map(({ _localId: _, ...rest }) => rest),
      });
      toast.success("Product created");
      navigate(`/products/${data.product_id}`);
    } catch (e) {
      const tierErr = parseTierLimitError(e);
      if (tierErr) {
        openUpgradeModal(tierErr);
      } else {
        toast.error("Failed to create");
      }
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="new-product-page">
      <div>
        <div className="font-mono text-xs uppercase tracking-widest text-[#4A4A4A]">New Entry</div>
        <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-black uppercase tracking-tight">Create Product</h1>
      </div>

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="brutal-card p-6 lg:col-span-2 space-y-4">
          <div>
            <label className="brutal-label">Name</label>
            <input className="brutal-input" value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} data-testid="product-name-input"/>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_180px] gap-4">
            <div>
              <label className="brutal-label">Categories</label>
              <CategoryPicker
                value={form.categories}
                onChange={(cats)=>setForm({...form, categories: cats})}
                testid="product-categories"
              />
            </div>
            <div>
              <label className="brutal-label">Low-stock Threshold</label>
              <input type="number" className="brutal-input" value={form.low_stock_threshold} onChange={(e)=>setForm({...form, low_stock_threshold: Number(e.target.value)})} data-testid="product-threshold-input"/>
            </div>
          </div>
          <div>
            <label className="brutal-label">Description</label>
            <textarea rows={3} className="brutal-input" value={form.description} onChange={(e)=>setForm({...form, description: e.target.value})} data-testid="product-description-input"/>
          </div>
        </div>

        <div className="brutal-card p-6">
          <label className="brutal-label">Photo</label>
          {form.photo_base64 ? (
            <img src={form.photo_base64} alt="" className="w-full aspect-square object-cover border-2 border-black"/>
          ) : (
            <label className="cursor-pointer block border-2 border-dashed border-black aspect-square flex items-center justify-center bg-[#F9F8F6]">
              <div className="text-center">
                <Upload size={24} className="mx-auto"/>
                <div className="mt-2 font-mono text-xs uppercase tracking-widest">Upload</div>
              </div>
              <input type="file" accept="image/*" onChange={onPhoto} className="hidden" data-testid="photo-upload-input"/>
            </label>
          )}
          {form.photo_base64 && (
            <button type="button" className="brutal-btn brutal-btn-secondary w-full mt-3" onClick={()=>setForm({...form, photo_base64: ""})}>
              Remove
            </button>
          )}
        </div>

        <div className="brutal-card p-6 lg:col-span-3">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-black uppercase">Variants</h2>
            <button type="button" className="brutal-btn brutal-btn-secondary text-xs" onClick={()=>setVariants((v)=>[...v, {_localId: crypto.randomUUID(), attributes:{}, stock:0, price:0}])} data-testid="add-variant-btn">
              <Plus size={12}/> Add Variant
            </button>
          </div>

          <div className="space-y-4">
            {variants.map((v, i) => (
              <div key={v._localId} className="brutal-card-sm p-4" data-testid={`variant-row-${i}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="font-mono text-xs uppercase tracking-widest">Variant #{i+1}</div>
                  {variants.length > 1 && (
                    <button type="button" className="brutal-badge !bg-[#D9381E] !text-white" onClick={()=>setVariants((prev)=>prev.filter((_, idx)=>idx!==i))}>
                      <Trash2 size={12}/>
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-[1fr_110px_110px_110px] gap-3 mb-3">
                  <div>
                    <label className="brutal-label">Attributes</label>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(v.attributes).map(([k, val]) => (
                        <div key={k} className="brutal-badge !py-1 flex items-center gap-2">
                          <span>{k}:</span>
                          <input
                            className="font-mono text-xs bg-transparent outline-none w-20"
                            value={val}
                            onChange={(e)=>setAttrValue(i, k, e.target.value)}
                            data-testid={`variant-${i}-attr-${k}`}
                          />
                          <button type="button" onClick={()=>removeAttr(i, k)} className="text-[#D9381E]">×</button>
                        </div>
                      ))}
                      <button type="button" className="brutal-badge" onClick={()=>addAttr(i)} data-testid={`add-attr-${i}`}>+ Attr</button>
                    </div>
                  </div>
                  <div>
                    <label className="brutal-label">Stock</label>
                    <input type="number" className="brutal-input" value={v.stock} onChange={(e)=>updateVariant(i, { stock: Number(e.target.value) })} data-testid={`variant-${i}-stock`}/>
                  </div>
                  <div>
                    <label className="brutal-label">Cost</label>
                    <input type="number" step="0.01" className="brutal-input" value={v.cost} onChange={(e)=>updateVariant(i, { cost: Number(e.target.value) })} data-testid={`variant-${i}-cost`} placeholder="0.00"/>
                  </div>
                  <div>
                    <label className="brutal-label">Price</label>
                    <input type="number" step="0.01" className="brutal-input" value={v.price} onChange={(e)=>updateVariant(i, { price: Number(e.target.value) })} data-testid={`variant-${i}-price`}/>
                  </div>
                </div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#4A4A4A]">
                  SKU &amp; barcode auto-generated on save
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-3 flex gap-3">
          <button type="submit" className="brutal-btn" disabled={saving} data-testid="save-product-btn">
            {saving ? "Saving…" : "Save Product"}
          </button>
          <button type="button" className="brutal-btn brutal-btn-secondary" onClick={()=>navigate(-1)}>Cancel</button>
        </div>
      </form>
    </div>
  );
}
